import { useState, useEffect } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import {
  X, MapPin, Users, Target, Copy, Phone, Mail,
  AlertTriangle, HeartHandshake, IndianRupee, ArrowRight, Landmark
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const ORG_PHONE = "+91 7669990505";
const ORG_EMAIL = "htstrust@gmail.com";

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selected,  setSelected]  = useState(null);
  const [donateFor, setDonateFor] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        setLoadError(false);
        const snap = await getDocs(
          query(collection(db, "campaigns"), orderBy("createdAt", "desc"))
        );
        setCampaigns(
          snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(c => c.active !== false)
        );
      } catch (e) {
        console.error("Campaigns load error:", e);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) return null;

  if (loadError) {
    return (
      <div className="camp-section">
        <div className="camp-inner" style={{ textAlign:"center", padding:"20px 0" }}>
          <p style={{ color:"#78716c", fontSize:13, marginBottom:10 }}>
            Couldn't load campaigns right now.
          </p>
          <button onClick={() => window.location.reload()} className="camp-donate-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (campaigns.length === 0) return null;

  function pct(c) {
    if (!c.goalAmount) return 0;
    return Math.min(100, Math.round(((c.raisedAmount || 0) / c.goalAmount) * 100));
  }

  // Featured = first urgent campaign, else just the newest one
  const featured = campaigns.find(c => c.urgent) || campaigns[0];
  const rest     = campaigns.filter(c => c.id !== featured.id);

  return (
    <>
      <style>{`
        .camp-section { background:#0d0c0a; border-top:1px solid rgba(255,255,255,0.05); border-bottom:1px solid rgba(255,255,255,0.05); padding:64px 24px; }
        .camp-inner { max-width:1280px; margin:0 auto; }
        .camp-label-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
        .camp-label-line { width:28px; height:2px; background:#f59e0b; border-radius:1px; flex-shrink:0; }
        .camp-label-text { font-size:12px; font-weight:800; color:#f59e0b; text-transform:uppercase; letter-spacing:2px; }
        .camp-title { font-family:'Playfair Display',serif; font-size:clamp(24px,4vw,42px); font-weight:800; color:#fef3e2; margin:0 0 36px; }

        /* ── FEATURED BANNER ── */
        .camp-featured {
          display:grid; grid-template-columns:1.1fr 1fr; gap:0;
          background:#111009; border:1px solid rgba(255,255,255,0.08);
          border-radius:20px; overflow:hidden; margin-bottom: ${rest.length ? "40px" : "0"};
        }
        .camp-featured-img-wrap { position:relative; min-height:340px; background:#1a1714; }
        .camp-featured-img { width:100%; height:100%; object-fit:cover; position:absolute; inset:0; }
        .camp-featured-urgent {
          position:absolute; top:16px; left:16px; display:flex; align-items:center; gap:5px;
          background:#ef4444; color:#fff; font-size:11px; font-weight:800; padding:5px 12px;
          border-radius:50px; text-transform:uppercase; letter-spacing:0.5px; z-index:2;
        }
        .camp-featured-body { padding:36px 40px; display:flex; flex-direction:column; justify-content:center; }
        .camp-featured-loc { display:flex; align-items:center; gap:6px; font-size:12px; color:#78716c; margin-bottom:10px; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; }
        .camp-featured-title { font-family:'Playfair Display',serif; font-size:clamp(22px,2.6vw,30px); font-weight:800; color:#fef3e2; margin:0 0 12px; line-height:1.25; }
        .camp-featured-summary { font-size:14px; color:#a8a29e; line-height:1.7; margin:0 0 26px; }
        .camp-progress-track { height:12px; background:rgba(255,255,255,0.07); border-radius:50px; overflow:hidden; margin-bottom:12px; }
        .camp-progress-fill { height:100%; border-radius:50px; background:linear-gradient(90deg,#f59e0b,#fbbf24); transition:width 0.8s ease; }
        .camp-numbers-row { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:6px; flex-wrap:wrap; gap:8px; }
        .camp-raised { font-family:'Playfair Display',serif; font-size:28px; font-weight:800; color:#fef3e2; }
        .camp-raised-lbl { font-size:12px; font-weight:600; color:#78716c; margin-left:6px; }
        .camp-goal { font-size:13px; color:#52504d; }
        .camp-meta-row { display:flex; gap:18px; margin-bottom:24px; flex-wrap:wrap; }
        .camp-meta-item { display:flex; align-items:center; gap:5px; font-size:12.5px; color:#78716c; }
        .camp-cta-row { display:flex; gap:12px; flex-wrap:wrap; }
        .camp-donate-btn {
          display:inline-flex; align-items:center; gap:8px; background:#f59e0b; color:#0a0908;
          border:none; border-radius:50px; padding:13px 26px; font-size:14px; font-weight:800;
          cursor:pointer; font-family:inherit; transition:transform 0.15s, box-shadow 0.15s;
        }
        .camp-donate-btn:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(245,158,11,0.35); }
        .camp-details-btn {
          display:inline-flex; align-items:center; gap:6px; background:transparent; color:#a8a29e;
          border:1px solid rgba(255,255,255,0.15); border-radius:50px; padding:13px 22px;
          font-size:14px; font-weight:700; cursor:pointer; font-family:inherit; transition:all 0.2s;
        }
        .camp-details-btn:hover { border-color:rgba(255,255,255,0.3); color:#fef3e2; }

        /* ── MORE CAMPAIGNS GRID ── */
        .camp-grid-label { font-size:12px; font-weight:800; color:#78716c; text-transform:uppercase; letter-spacing:1.5px; margin-bottom:16px; }
        .camp-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(280px,1fr)); gap:16px; }
        .camp-card {
          background:#111009; border:1px solid rgba(255,255,255,0.07); border-radius:14px;
          overflow:hidden; cursor:pointer; transition:transform 0.2s, border-color 0.2s;
        }
        .camp-card:hover { transform:translateY(-4px); border-color:rgba(255,255,255,0.15); }
        .camp-card-img-wrap { position:relative; padding-top:56%; overflow:hidden; background:#1a1714; }
        .camp-card-img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
        .camp-card-urgent {
          position:absolute; top:10px; left:10px; display:flex; align-items:center; gap:4px;
          background:#ef4444; color:#fff; font-size:10px; font-weight:800; padding:3px 9px;
          border-radius:50px; text-transform:uppercase;
        }
        .camp-card-body { padding:16px 18px 20px; }
        .camp-card-title { font-size:15px; font-weight:700; color:#fef3e2; margin:0 0 10px; line-height:1.4; }
        .camp-card-track { height:8px; background:rgba(255,255,255,0.06); border-radius:50px; overflow:hidden; margin-bottom:8px; }
        .camp-card-fill { height:100%; border-radius:50px; background:linear-gradient(90deg,#f59e0b,#fbbf24); }
        .camp-card-row { display:flex; justify-content:space-between; font-size:11.5px; color:#78716c; margin-bottom:14px; }
        .camp-card-btn {
          width:100%; padding:9px; background:rgba(245,158,11,0.1); color:#f59e0b;
          border:1px solid rgba(245,158,11,0.25); border-radius:9px; font-size:12.5px; font-weight:700;
          cursor:pointer; font-family:inherit;
        }

        /* ── MOBILE ── */
        @media(max-width:768px) {
          .camp-section { padding:40px 16px; }
          .camp-featured { grid-template-columns:1fr; border-radius:16px; }
          .camp-featured-img-wrap { min-height:220px; }
          .camp-featured-body { padding:26px 22px; }
          .camp-title { margin-bottom:24px; }
          .camp-grid { grid-template-columns:1fr; }
        }

        /* ── MODALS ── */
        .camp-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.9); z-index:9999; display:flex; align-items:center; justify-content:center; padding:20px; backdrop-filter:blur(10px); }
        .camp-modal { background:#111009; border:1px solid rgba(255,255,255,0.1); border-radius:20px; padding:24px; width:100%; max-width:560px; position:relative; max-height:92vh; overflow-y:auto; }
        .camp-close-btn { position:absolute; top:14px; right:14px; background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.1); border-radius:50%; width:34px; height:34px; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#a8a29e; z-index:1; }
        .camp-modal-title { font-family:'Playfair Display',serif; font-size:22px; font-weight:800; color:#fef3e2; margin:0 0 4px; }
        .camp-modal-loc { display:flex; align-items:center; gap:5px; font-size:12px; color:#78716c; margin-bottom:8px; }
        .camp-story { font-size:14px; color:#a8a29e; line-height:1.75; margin-top:16px; white-space:pre-wrap; }
        .camp-copy-row { display:flex; align-items:center; gap:12px; padding:12px 14px; background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:12px; margin-bottom:10px; }
        .camp-copy-icon { width:34px; height:34px; border-radius:9px; background:rgba(245,158,11,0.12); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .camp-copy-label { font-size:10px; font-weight:700; color:#52504d; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:2px; }
        .camp-copy-val { font-size:13px; font-weight:600; color:#fef3e2; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .camp-copy-btn { width:32px; height:32px; border-radius:8px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.1); color:#a8a29e; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .camp-copy-divider { font-size:10px; font-weight:800; color:#52504d; text-transform:uppercase; letter-spacing:1px; margin:14px 0 8px; }
      `}</style>

      <div className="camp-section">
        <Toaster position="top-center" />
        <div className="camp-inner">
          {/* Header */}
          <div className="camp-label-row">
            <span className="camp-label-line" />
            <span className="camp-label-text">Make An Impact</span>
          </div>
          <h2 className="camp-title">Active Campaigns</h2>

          {/* Featured Banner */}
          <div className="camp-featured">
            <div className="camp-featured-img-wrap">
              {featured.urgent && (
                <div className="camp-featured-urgent"><AlertTriangle size={12} /> Urgent</div>
              )}
              <img
                src={featured.imageUrl}
                alt={featured.title}
                className="camp-featured-img"
                onError={e => { e.target.style.display = "none"; }}
              />
            </div>
            <div className="camp-featured-body">
              {featured.location && (
                <div className="camp-featured-loc"><MapPin size={12} color="#f59e0b" /> {featured.location}</div>
              )}
              <h3 className="camp-featured-title">{featured.title}</h3>
              {featured.summary && <p className="camp-featured-summary">{featured.summary}</p>}

              <div className="camp-progress-track">
                <div className="camp-progress-fill" style={{ width: `${pct(featured)}%` }} />
              </div>
              <div className="camp-numbers-row">
                <span><span className="camp-raised">₹{(featured.raisedAmount || 0).toLocaleString()}</span><span className="camp-raised-lbl">raised</span></span>
                <span className="camp-goal">of ₹{(featured.goalAmount || 0).toLocaleString()} goal</span>
              </div>
              <div className="camp-meta-row">
                <span className="camp-meta-item"><Target size={12} color="#f59e0b" /> {pct(featured)}% funded</span>
                {typeof featured.donorCount === "number" && (
                  <span className="camp-meta-item"><Users size={12} color="#f59e0b" /> {featured.donorCount} donors</span>
                )}
              </div>

              <div className="camp-cta-row">
                <button className="camp-donate-btn" onClick={() => setDonateFor(featured)}>
                  <HeartHandshake size={15} /> Donate Now
                </button>
                {featured.story && (
                  <button className="camp-details-btn" onClick={() => setSelected(featured)}>
                    Read Full Story <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Additional campaigns */}
          {rest.length > 0 && (
            <>
              <div className="camp-grid-label">More Campaigns</div>
              <div className="camp-grid">
                {rest.map(c => (
                  <div key={c.id} className="camp-card" onClick={() => setSelected(c)}>
                    <div className="camp-card-img-wrap">
                      {c.urgent && <div className="camp-card-urgent"><AlertTriangle size={10} /> Urgent</div>}
                      <img src={c.imageUrl} alt={c.title} className="camp-card-img"
                        onError={e => { e.target.style.display = "none"; }} />
                    </div>
                    <div className="camp-card-body">
                      <h4 className="camp-card-title">{c.title}</h4>
                      <div className="camp-card-track">
                        <div className="camp-card-fill" style={{ width: `${pct(c)}%` }} />
                      </div>
                      <div className="camp-card-row">
                        <span>₹{(c.raisedAmount || 0).toLocaleString()} raised</span>
                        <span>{pct(c)}% funded</span>
                      </div>
                      <button className="camp-card-btn" onClick={e => { e.stopPropagation(); setDonateFor(c); }}>
                        Donate Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="camp-overlay" onClick={() => setSelected(null)}>
          <div className="camp-modal" onClick={e => e.stopPropagation()}>
            <button className="camp-close-btn" onClick={() => setSelected(null)}><X size={16} /></button>
            <img
              src={selected.imageUrl}
              alt={selected.title}
              style={{ width: "100%", maxHeight: "45vh", objectFit: "cover", borderRadius: 12, marginBottom: 18 }}
              onError={e => { e.target.style.display = "none"; }}
            />
            {selected.location && (
              <div className="camp-modal-loc"><MapPin size={12} color="#f59e0b" /> {selected.location}</div>
            )}
            <h3 className="camp-modal-title">{selected.title}</h3>

            <div className="camp-progress-track" style={{ marginTop: 14 }}>
              <div className="camp-progress-fill" style={{ width: `${pct(selected)}%` }} />
            </div>
            <div className="camp-numbers-row">
              <span><span className="camp-raised" style={{ fontSize: 20 }}>₹{(selected.raisedAmount || 0).toLocaleString()}</span><span className="camp-raised-lbl">raised</span></span>
              <span className="camp-goal">of ₹{(selected.goalAmount || 0).toLocaleString()} goal</span>
            </div>
            <div className="camp-meta-row">
              <span className="camp-meta-item"><Target size={12} color="#f59e0b" /> {pct(selected)}% funded</span>
              {typeof selected.donorCount === "number" && (
                <span className="camp-meta-item"><Users size={12} color="#f59e0b" /> {selected.donorCount} donors</span>
              )}
            </div>

            {selected.story && <p className="camp-story">{selected.story}</p>}

            <button className="camp-donate-btn" style={{ marginTop: 18, width: "100%", justifyContent: "center" }}
              onClick={() => setDonateFor(selected)}>
              <HeartHandshake size={15} /> Donate to this Campaign
            </button>
          </div>
        </div>
      )}

      {/* Donate Modal */}
      {donateFor && (
        <div className="camp-overlay" onClick={() => setDonateFor(null)}>
          <div className="camp-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <button className="camp-close-btn" onClick={() => setDonateFor(null)}><X size={16} /></button>
            <h3 className="camp-modal-title">Support "{donateFor.title}"</h3>
            <p style={{ fontSize: 13, color: "#78716c", lineHeight: 1.6, margin: "8px 0 18px" }}>
              Reach out using any option below. Once received, our team logs it against this
              campaign and the progress bar updates.
            </p>

            {donateFor.upiId && <CopyRow icon={IndianRupee} label="UPI ID" value={donateFor.upiId} />}

            {donateFor.accountNumber && (
              <>
                <div className="camp-copy-divider">Bank Transfer</div>
                {donateFor.accountName && <CopyRow icon={Landmark} label="Account Name" value={donateFor.accountName} />}
                <CopyRow icon={Landmark} label="Account Number" value={donateFor.accountNumber} />
                {donateFor.ifscCode && <CopyRow icon={Landmark} label="IFSC Code" value={donateFor.ifscCode} />}
                {donateFor.bankName && <CopyRow icon={Landmark} label="Bank" value={donateFor.bankName} />}
              </>
            )}

            <div className="camp-copy-divider">Contact</div>
            <CopyRow icon={Phone} label="Call / WhatsApp" value={donateFor.contactPhone || ORG_PHONE} />
            <CopyRow icon={Mail} label="Email" value={ORG_EMAIL} />
          </div>
        </div>
      )}
    </>
  );
}

function CopyRow({ icon: Icon, label, value }) {
  function copy() {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied!`);
  }
  return (
    <div className="camp-copy-row">
      <div className="camp-copy-icon"><Icon size={15} color="#f59e0b" /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="camp-copy-label">{label}</div>
        <div className="camp-copy-val">{value}</div>
      </div>
      <button onClick={copy} className="camp-copy-btn"><Copy size={14} /></button>
    </div>
  );
}